import React from "react";
import { Result, SemgrepErrors } from "../../../types";
import { CONFIG_DOC_LINK, PATTERN_DOC_LINK } from "../../../util";
import { Button, Icon, Spinner } from "@blueprintjs/core";
import styled from "styled-components";
import { Language } from "../../../lang";

interface Props {
  results: Result[] | undefined;
  errorMsg?: string;
  errors: SemgrepErrors | undefined;
  pattern: string;
  numTestFailures?: number | null;
  language: Language;
  loading: boolean;
  onClose: () => void;
}

const Console = styled.div`
  position: absolute;
  right: 8px;
  bottom: 36px;
  background-color: #000000;
  font-family: var(--global-font-family-monospace);
  font-size: var(--global-font-size-xs);
  color: var(--global-light-gray);
  padding: 8px;
  width: 200px;
  max-height: 300px;
  border-radius: 5px;
`;

const Header = styled.div`
  width: 100%;
  color: #7a6bcb;
  font-size: var(--global-font-size-xs);
  margin-bottom: 8px;
  position: relative;
`;

const Content = styled.div`
  overflow: scroll;
  max-height: 264px;
`;

const LeftAlignButton = styled(Button)`
  min-height: 0;
  min-width: 0;
  width: 16px;
  padding: 0;
  position: absolute;
  right: 4px;
  top: 2px;
`;

const ErrorDiv = styled.div`
  color: var(--global-red);
`;

const errorsExist = (errors?: SemgrepErrors): boolean => {
  return errors
    ? errors.internalErrors.length > 0 ||
        errors.parseCodeErrors.length > 0 ||
        errors.parsePatternErrors.length > 0
    : false;
};

const invalidPatternHelp = (
  <div>
    The{" "}
    <a href={PATTERN_DOC_LINK} target="_blank" rel="noopener noreferrer">
      pattern documentation
    </a>{" "}
    has full details on pattern syntax.
  </div>
);

const noMatchesHelp = (
  <div>
    The{" "}
    <a href={CONFIG_DOC_LINK} target="_blank" rel="noopener noreferrer">
      rule documentation
    </a>{" "}
    has full details on rule syntax and boolean logic.
  </div>
);

export default class Results extends React.PureComponent<Props, {}> {
  public render() {
    const { results, errorMsg, errors, numTestFailures, loading, onClose } =
      this.props;

    const noMatches = results && results.length === 0 && !errorsExist(errors);

    const spanErrors =
      (errors && errors.parseCodeErrors.concat(errors.parsePatternErrors)) ||
      [];
    const internalErrors = (errors && errors.internalErrors) || [];

    return (
      <Console>
        <Header>
          OUTPUT
          <LeftAlignButton minimal onClick={onClose}>
            <Icon icon={"cross"} />
          </LeftAlignButton>
        </Header>
        <Content>
          {internalErrors.length > 0 && (
            <div>
              {internalErrors.map((error) => (
                <ErrorDiv>
                  {JSON.stringify(error).includes("MatchTimeoutError")
                    ? "Semgrep Timed Out"
                    : JSON.stringify(error).replace(/(^")|("$)/g, "")}
                </ErrorDiv>
              ))}
              {invalidPatternHelp}
            </div>
          )}

          {results === undefined ? (
            <>
              {errorMsg ? (
                <ErrorDiv>{errorMsg}</ErrorDiv>
              ) : (
                <div>Run to see results</div>
              )}
            </>
          ) : noMatches ? (
            <>
              <ErrorDiv>No matches</ErrorDiv>
              {noMatchesHelp}
            </>
          ) : (
            <>
              {loading ? (
                <Spinner intent="primary" />
              ) : (
                <>
                  {internalErrors.length === 0 && (
                    <>
                      {spanErrors.map((error) => (
                        <div>{error.message}</div>
                      ))}

                      {results.map((r) => (
                        <div>
                          <span style={{ color: "var(--global-green)" }}>
                            L{r.start.line}:
                          </span>
                          {` ${r.message}`}
                        </div>
                      ))}
                    </>
                  )}
                  {numTestFailures !== null && numTestFailures !== undefined && (
                    <>
                      {numTestFailures === 0 ? (
                        <div style={{ color: "var(--global-green)" }}>
                          <br />
                          All tests pass
                        </div>
                      ) : (
                        <ErrorDiv>
                          <br />
                          {numTestFailures}{" "}
                          {numTestFailures === 1 ? "test" : "tests"} failed
                        </ErrorDiv>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}
        </Content>
      </Console>
    );
  }
}
